import {
    Button,
    FormControl,
    Grid,    
    InputLabel,
    MenuItem,
    Paper,    
    Select,    
} from '@material-ui/core';

import { MaterialTableProps } from 'material-table';
import React, { FunctionComponent, useEffect } from 'react';
import styles from './ClientReport.Styles';
import MaterialTable from '../../generic-components/material-table/MaterialTable.Component';
import ClientReportDataModel from '../../../data-models/ClientReport.DataModel';
import TenantEntity from '../../../entities/Tenant.Entity';
import { Loader } from '../../generic-components/loader/Loader.Component';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import FileDownload from '@material-ui/icons/GetApp';

interface ClientReportsProps {
    clientReports: Array<ClientReportDataModel>;
    tenants: Array<TenantEntity>;    
    handleGetClientReportsTable: (filters: ClientReportSearchFilter) => void;
    isLoading: boolean;
}

export interface ClientReportSearchFilter {
    tenantId: number;    
}

export const ClientReports: FunctionComponent<ClientReportsProps> = (props) => {
    const classes = styles();
    const [filter, setFilter] = React.useState<boolean>(false);    
    const [clientReportSearchFilter, setClientReportSearchFilter] = React.useState<ClientReportSearchFilter>({
        tenantId: null,
    });
        
    const clientReportTableProps: MaterialTableProps<ClientReportDataModel> = {
       columns: [
           {
               title: 'Client ID',
               field: 'clientId',
           },
           {
               title: 'Client Number',
               field: 'clientNumber',
           },
           {
               title: 'Client Name',
               field: 'clientName',
           },
           {
               title: 'Contact Name',
               field: 'contactName',
           },
           {
               title: 'Jurisdiction State',
               field: 'jurisdictionState',
           },
           {
               title: 'IME - Client Preferred Vendor',
               field: 'imeClientPreferredVendor'            
           },
           {
               title: 'IME - CCMSI Vendor Partner',
               field: 'imeCcmsiVendorPartner'              
           },
           {
               title: 'PM - Client Preferred Vendor',
               field: 'pmClientPreferredVendor',
           },
           {
               title: 'PM - CCMSI Vendor Partner',
               field: 'pmCcmsiVendorPartner',
           },
           {
               title: 'DX - Client Preferred Vendor',
               field: 'dxClientPreferredVendor',
           },
           {
               title: 'DX - CCMSI Vendor Partner',
               field: 'dxCcmsiVendorPartner',
           },
           {
               title: 'UR - Client Preferred Vendor',
               field: 'urClientPreferredVendor',
           },
           {
               title: 'UR - CCMSI Vendor Partner',
               field: 'urCcmsiVendorPartner',
           },
           {
               title: 'TT - Client Preferred Vendor',
               field: 'ttClientPreferredVendor',
           },
           {
               title: 'TT - CCMSI Vendor Partner',
               field: 'ttCcmsiVendorPartner',
           },
           {
               title: 'DME - Client Preferred Vendor',
               field: 'dmeClientPreferredVendor',
           },
           {
               title: 'DME - CCMSI Vendor Partner',
               field: 'dmeCcmsiVendorPartner',
           },
       ],
       data: props.clientReports ?? [],
       options: {
           filtering: filter,
           showTitle: true,
           paging: props.clientReports ? props.clientReports.length > 100 : false,           
           toolbar: true,
           exportButton: false,
           exportFileName: 'TerraClaimConnect_Admin_ClientReport',
           pageSizeOptions: [5, 10, 20, 50, 100],
           pageSize: 100,           
           paginationPosition: 'top',
           emptyRowsWhenPaging: false,
           toolbarButtonAlignment: 'right',
           searchFieldAlignment: 'right',
           searchFieldVariant: 'standard',
           searchFieldStyle: {
               marginRight: '0px',
               width: '200px'
           },
           headerStyle: {
               textAlign: 'left'
           },
           maxBodyHeight: 'calc(100vh - 450px)',
           tableLayout: 'fixed',
           fixedColumns: {
               left: 0,
               right: 0
           },
           padding: 'dense',
           rowStyle: {
               height: '40px'
           }
       },   
       actions: [
         {
           icon: () => <FileDownload />,
           tooltip: 'Export as CSV',
           isFreeAction: true, 
           onClick: (event, rowData) => exportToCSV()
         },        
         {
           icon: 'tune',
           tooltip: 'Filter',
           isFreeAction: true,
           onClick: () => setFilter(!filter)
         } 
       ],       
       title: 'Client Report',    
    };

    const exportToCSV = () => {
       const fileName = 'TerraClaimConnect_Admin_ClientReport.csv';       
       let csvContent = '';
       const customColumns = clientReportTableProps.columns;
       
       // Add the column titles
       const headers = customColumns.map(column => column.title).join(',');
       csvContent += headers + "\r\n";

       // Add the data rows
       props.clientReports?.forEach(result => {
           const row = customColumns.map(column => {
               const value = result[column.field] ?? '';
               const safeValue = typeof value === 'string' ? value.replace(/"/g, '""') : value;
               return `"${safeValue}"`;                
           }).join(',');
           csvContent += row + "\r\n";
       });
       
       const encodedUri = "data:text/csv;charset=utf-8," + encodeURIComponent(csvContent);
       const link = document.createElement("a");
       link.setAttribute("href", encodedUri);
       link.setAttribute("download", fileName);
       document.body.appendChild(link);
       link.click();
       document.body.removeChild(link);
    };    

    const handleTextFieldChange = (
        event:
            | React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
            | React.ChangeEvent<{ name?: string; value: unknown; id?: string }>,
        ) => {
            event.persist();
            let newFilter: ClientReportSearchFilter = { ...clientReportSearchFilter };
            newFilter[event.target.id ? event.target.id : event.target.name] =
                event.target.value == 'true' ? true : event.target.value == 'false' ? false : event.target.value;
            setClientReportSearchFilter(newFilter);
        };

    return (
        <Paper className={classes.paper}>            
            <Grid container spacing={3} className={classes.gridContainer}>
                <Grid item xs={2}>
                    <FormControl className={classes.selectInput}>                    
                        <InputLabel id="demo-simple-select-label">Tenant</InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="tenantId"
                            name="tenantId"
                            value={clientReportSearchFilter.tenantId || ""}
                            onChange={(event) => handleTextFieldChange(event)}
                        >
                            {props.tenants?.map((tenant, index) => {
                                return (
                                    <MenuItem key={tenant.tenantId} value={tenant.tenantId}>
                                        {tenant.tenantName}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                </Grid>
                
                <Button 
                    className={classes.button} 
                    variant="contained" 
                    onClick={() => props.handleGetClientReportsTable(clientReportSearchFilter)}
                >
                    Get Report
                </Button>                
            </Grid>
            
            {props.isLoading ? <Loader isSmall /> : <MaterialTable tableProps={clientReportTableProps} />}
        </Paper>
     );
 };